import { useEffect, useRef, useState } from "react";
import { MousePointer2 } from "lucide-react";

// Mouse-drag glitchy pixelated image.
// Drag on the canvas → the harder/faster you drag, the more the image glitches.
// - Renders a heavily pixelated version of `src`
// - RGB split + horizontal slice glitches based on drag intensity
// - Intensity smoothly decays when not dragging
// - Works with any image URL (no CORS/getImageData; uses drawImage compositing only)
export default function MicGlitchImage({ src, pixelSize = 8, aspect = 1, testid = "mic-glitch" }) {
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const rafRef = useRef(0);
    const intensityRef = useRef(0);
    const lastPointRef = useRef(null);
    const draggingRef = useRef(false);
    const [ready, setReady] = useState(false);
    const [err, setErr] = useState("");

    // Load image (no crossOrigin — we never read pixels, we just drawImage them)
    useEffect(() => {
        if (!src) return;
        setErr("");
        setReady(false);
        const img = new Image();
        img.referrerPolicy = "no-referrer";
        img.onload = () => {
            imgRef.current = img;
            setReady(true);
        };
        img.onerror = () => setErr("image failed to load");
        img.src = src;
    }, [src]);

    // Draw loop
    useEffect(() => {
        if (!ready) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const img = imgRef.current;

        const off = document.createElement("canvas");
        const offCtx = off.getContext("2d");

        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = Math.floor(rect.width * dpr);
            canvas.height = Math.floor(rect.width * aspect * dpr);
            const w = Math.max(4, Math.floor(canvas.width / pixelSize));
            const h = Math.max(4, Math.floor(canvas.height / pixelSize));
            off.width = w;
            off.height = h;
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        const draw = () => {
            // Decay intensity every frame
            intensityRef.current = Math.max(0, intensityRef.current - 0.015);
            const intensity = Math.min(1, intensityRef.current);

            // 1. Pixelate: draw to small offscreen, then back up-scaled
            offCtx.imageSmoothingEnabled = false;
            offCtx.clearRect(0, 0, off.width, off.height);
            offCtx.drawImage(img, 0, 0, off.width, off.height);

            ctx.imageSmoothingEnabled = false;
            ctx.fillStyle = "#000";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(off, 0, 0, canvas.width, canvas.height);

            if (intensity > 0.02) {
                // 2. Horizontal slice shifts: re-draw slices of the pixelated
                //    offscreen back to the main canvas at random X offsets.
                const slices = 3 + Math.floor(intensity * 10);
                const maxShift = Math.floor(intensity * canvas.width * 0.22);
                for (let i = 0; i < slices; i += 1) {
                    const sy = Math.random() * off.height;
                    const sh = 1 + Math.random() * (off.height * 0.08);
                    const dy = sy * pixelSize;
                    const dh = sh * pixelSize;
                    const dx = (Math.random() * 2 - 1) * maxShift;
                    ctx.drawImage(
                        off,
                        0, sy, off.width, sh,
                        dx, dy, canvas.width, dh
                    );
                }

                // 3. RGB channel split (chromatic aberration) — draw offset color washes
                const dx = Math.max(2, intensity * 18);
                ctx.globalCompositeOperation = "screen";
                ctx.globalAlpha = 0.6 * intensity;
                ctx.fillStyle = "#FF0033";
                ctx.fillRect(dx, 0, canvas.width, canvas.height);
                ctx.fillStyle = "#00E5FF";
                ctx.fillRect(-dx, 0, canvas.width, canvas.height);
                ctx.globalAlpha = 1;
                ctx.globalCompositeOperation = "source-over";

                // 4. Scan-line noise
                if (Math.random() < intensity) {
                    const y = Math.random() * canvas.height;
                    ctx.fillStyle = "rgba(255,255,255,0.08)";
                    ctx.fillRect(0, y, canvas.width, 1 + Math.random() * 3);
                }
            }

            rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(rafRef.current);
            ro.disconnect();
        };
    }, [ready, pixelSize, aspect]);

    // Mouse/touch handlers
    const pointFromEvent = (e) => {
        const t = e.touches ? e.touches[0] : e;
        return { x: t.clientX, y: t.clientY };
    };
    const onDown = (e) => {
        draggingRef.current = true;
        lastPointRef.current = pointFromEvent(e);
        intensityRef.current = Math.min(1, intensityRef.current + 0.15);
        e.preventDefault();
    };
    const onMove = (e) => {
        if (!draggingRef.current) return;
        const p = pointFromEvent(e);
        const lp = lastPointRef.current || p;
        const dx = p.x - lp.x;
        const dy = p.y - lp.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        // Add intensity proportional to drag velocity (clamped)
        intensityRef.current = Math.min(1, intensityRef.current + Math.min(0.12, dist / 200));
        lastPointRef.current = p;
    };
    const onUp = () => {
        draggingRef.current = false;
        lastPointRef.current = null;
    };

    useEffect(() => {
        window.addEventListener("mouseup", onUp);
        window.addEventListener("touchend", onUp);
        return () => {
            window.removeEventListener("mouseup", onUp);
            window.removeEventListener("touchend", onUp);
        };
    }, []);

    return (
        <div className="relative w-full" data-testid={testid}>
            <canvas
                ref={canvasRef}
                data-testid={`${testid}-canvas`}
                onMouseDown={onDown}
                onMouseMove={onMove}
                onTouchStart={onDown}
                onTouchMove={onMove}
                className="block w-full bg-[#000] touch-none cursor-crosshair"
                style={{ aspectRatio: `1 / ${aspect}` }}
            />
            <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 px-3 py-2 bg-[#FFFFFF] text-[#333] font-mono text-[10px] uppercase tracking-[0.25em] pointer-events-none">
                <MousePointer2 size={12} /> click &amp; drag
            </div>
            {err && (
                <div className="absolute bottom-3 right-3 px-2 py-1 bg-[#333] text-[#FFFFFF] font-mono text-[9px] uppercase tracking-[0.2em]" data-testid={`${testid}-error`}>
                    {err}
                </div>
            )}
        </div>
    );
}
